import { CdkStep, CdkStepper } from "@angular/cdk/stepper";
import { AfterViewInit, ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { CrmProjectUpdateModalComponent } from "@components/crm-project-update-modal/crm-project-update-modal.component";
import { UploadedFloorplan } from "@components/floorplan-upload-overlay/uploaded-floorplan";
import { ContactPersonFormComponent } from "@components/project-forms/contact-person-form/contact-person-form.component";
import { CustomerFormComponent } from "@components/project-forms/customer-form/customer-form.component";
import { GoodsRecipientFormComponent } from "@components/project-forms/goods-recipient/goods-recipient-form.component";
import { ProjectFormComponent } from "@components/project-forms/project-form/project-form.component";
import { ProjectFormsService } from "@components/project-forms/project-forms.service";
import { NewProjectStepperComponent } from "@components/stepper/new-project-stepper/new-project-stepper.component";
import { CrmEntry } from "@domain/crm/crm";
import { CrmProjectMapperService } from "@domain/crm/crm-project-mapper.service";
import { CrmService } from "@domain/crm/crm.service";
import { ProductCountryService } from "@domain/product/product-country.service";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { ContactPerson } from "@domain/project/contact-person";
import { Customer } from "@domain/project/customer";
import { GoodsRecipient } from "@domain/project/goods-recipient";
import { createProjectByScope } from "@domain/project/project-deserializer";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { isBusinessScope, isFgdsProject, isMonitoringProject } from "@domain/project/project-guards";
import { ProjectImageData, ProjectImageService } from "@domain/project/project-image.service";
import { ProjectService } from "@domain/project/project.service";
import { TemplateDto } from "@domain/project/template";
import { TemplateService } from "@domain/project/template/template.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { NewProjectFloorplanUploadComponent } from "@pages/new-project/new-project-floorplan-upload/new-project-floorplan-upload.component";
import { NewProjectImageUploadComponent } from "@pages/new-project/new-project-image-upload/new-project-image-upload.component";
import { ToastHelper } from "@utils/toast-helper.service";
import { BehaviorSubject, catchError, combineLatest, EMPTY, filter, map, shareReplay, switchMap, take, tap } from "rxjs";

@Component({
  selector: "app-new-project",
  templateUrl: "./new-project.component.html",
  styleUrls: ["./new-project.component.scss"],
  imports: [
    UiKitModule,
    RouterModule,
    ReactiveFormsModule,
    CdkStep,
    NewProjectStepperComponent,
    ProjectFormComponent,
    CustomerFormComponent,
    ContactPersonFormComponent,
    GoodsRecipientFormComponent,
    NewProjectFloorplanUploadComponent,
    NewProjectImageUploadComponent,
  ],
})
export class NewProjectComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private projectService = inject(ProjectService);
  private router = inject(Router);
  private projectFormsService = inject(ProjectFormsService);
  private crmProjectMapperService = inject(CrmProjectMapperService);
  private cdr = inject(ChangeDetectorRef);
  private productCountryService = inject(ProductCountryService);
  private userService = inject(UserService);
  private toastHelper = inject(ToastHelper);
  private route = inject(ActivatedRoute);
  private crmService = inject(CrmService);
  private modalService = inject(ModalService);
  private templateService = inject(TemplateService);
  private readonly projectImageService = inject(ProjectImageService);
  private readonly projectFloorplanService = inject(ProjectFloorplanService);
  private readonly normalizeOptionalString = (value: unknown) => this.projectFormsService.normalizeOptionalString(value);

  projectForm?: FormGroup;
  customerForm?: FormGroup;
  contactPersonForm?: FormGroup;
  goodsRecipientForm?: FormGroup;
  imageForm?: FormGroup;
  floorplanForm?: FormGroup;
  crmEntry?: CrmEntry;
  userScope?: BusinessScope;

  private readonly selectedCountry$ = new BehaviorSubject<string | null>(null);

  private template$ = combineLatest([this.userService.businessScope$, this.selectedCountry$]).pipe(
    filter((pair): pair is [BusinessScope, string] => !!pair[0] && !!pair[1]),
    switchMap(([scope, country]) => this.templateService.generateTemplate(scope, country)),
    filter((template): template is TemplateDto => !!template),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly template = toSignal(this.template$, {
    initialValue: null,
  });

  templateFields = computed(() => {
    const template = this.template();
    if (!template) return null;

    return template.properties?.project ?? null;
  });

  protected isGoodsRecipientInformationVisible = computed<boolean | undefined>(() => {
    const template = this.template();
    return template?.properties?.project?.details?.goodsRecipient?.information?.visible;
  });

  @ViewChild("stepper")
  private stepper!: CdkStepper;

  // Needed to show validation errors on submit without userinput
  @ViewChild("projectFormComponent")
  projectFormComponent!: ProjectFormComponent;
  @ViewChild("customerFormComponent")
  customerFormComponent!: CustomerFormComponent;
  @ViewChild("contactPersonFormComponent")
  contactPersonFormComponent!: ContactPersonFormComponent;
  @ViewChild("goodsRecipientFormComponent")
  goodsRecipientFormComponent!: GoodsRecipientFormComponent;
  @ViewChild("imageFormComponent")
  imageUploadComponent!: NewProjectImageUploadComponent;
  @ViewChild("floorplanFormComponent")
  floorplanFormComponent!: NewProjectFloorplanUploadComponent;

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        switchMap((params) => {
          const crmOpportunityId = params["crmOpportunityId"];
          const crmContactId = params["crmContactId"];
          const crmId = crmOpportunityId ?? crmContactId;
          if (!crmId) return EMPTY;

          return this.crmService.findById({ opportunityId: crmOpportunityId, contactId: crmContactId }).pipe(
            catchError(() => {
              this.toastHelper.error(
                $localize`:@@toast.crmProjectNotFound.header:CRM-Daten konnten nicht geladen werden`,
                $localize`:@@toast.crmProjectNotFound.message:Für die CRM-ID ${crmId}:crmId: wurden keine importierbaren Daten gefunden. Sie können das Projekt wie gewohnt manuell anlegen.`,
              );
              this.router.navigate(["/new-project"]);
              return EMPTY;
            }),
          );
        }),
        switchMap((crmEntry) => {
          if (!crmEntry) return EMPTY;
          this.crmEntry = crmEntry;
          return this.crmProjectMapperService.createProjectFromCrmEntry(crmEntry);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((project) => {
        if (!project) return;
        this.projectFormsService.initializeProject(project);
        this.toastHelper.success(
          $localize`:@@toast.crmProjectImportSuccessful.header:CRM-Daten wurden übernommen`,
          $localize`:@@toast.crmProjectImportSuccessful.message:Die verfügbaren CRM-Daten wurden in die Projektfelder eingefügt.`,
        );
      });

    this.userService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (isBusinessScope(user.scope)) {
        this.userScope = user.scope;
      }
    });
  }

  ngAfterViewInit() {
    this.projectService.updateUserWritePermission(undefined, this.router.url).subscribe();
    // to display correct number of total steps
    this.cdr.detectChanges();
  }

  onCountryChange(country: string) {
    this.selectedCountry$.next(country);
  }

  onSubmit() {
    if (this.stepper.selectedIndex == 0) {
      this.projectFormComponent.triggerSubmit();
      this.customerFormComponent.triggerSubmit();
      this.contactPersonFormComponent.triggerSubmit();
      this.goodsRecipientFormComponent?.triggerSubmit();

      if (
        !this.projectForm!.valid ||
        !this.customerForm!.valid ||
        !this.contactPersonForm!.valid ||
        (this.goodsRecipientForm && !this.goodsRecipientForm.valid)
      ) {
        return;
      }
    }

    if (this.stepper.selectedIndex == 1) {
      this.floorplanFormComponent.triggerSubmit();
      if (!this.floorplanForm?.valid) {
        return;
      }
    }

    if (this.stepper.selectedIndex == 2) {
      this.imageUploadComponent.triggerSubmit();
      if (!this.imageForm?.valid) {
        return;
      }
    }

    if (this.stepper.selectedIndex === this.stepper.steps.length - 1) {
      if (!isBusinessScope(this.userScope)) {
        this.toastHelper.error(
          $localize`:@@toast.scopeMissing.header:Bereich konnte nicht ermittelt werden`,
          $localize`:@@toast.scopeMissing.message:Bitte laden Sie die Seite neu und versuchen Sie es erneut.`,
        );
        return;
      }

      const newProject = this.mapProject();
      this.projectService
        .createProject(newProject)
        .pipe(
          tap((project) => this.productCountryService.selectCountry(project.country)),
          take(1),
        )
        .subscribe((project) => {
          this.router.navigate([`/projects/${project.id}`]);
        });
    } else {
      this.stepper.next();
    }
  }

  protected openCRMProjectUpdateModal() {
    this.modalService.open(CrmProjectUpdateModalComponent, { size: ModalSize.LARGE }).onClose$.subscribe((data) => {
      if (!this.crmEntry) return;
      this.linkCrmEntryToProject(this.crmEntry!, data);
    });
  }

  private linkCrmEntryToProject(crmEntry: CrmEntry, metadata: CloudProjectMetadata) {
    this.modalService
      .open(ConfirmModalComponent, {
        data: {
          text: $localize`:@@modal.crmProject.overwriteProject:Wollen Sie die Projektinformationen wirklich mit den Daten aus dem CRM überschreiben?`,
        },
      })
      .onClose$.pipe(
        switchMap(() => {
          return this.projectService.loadProjectFromCloud(metadata.id);
        }),
        switchMap((project) => {
          this.crmProjectMapperService.overwriteProjectWithCrmEntry(project, crmEntry);
          return this.projectService.update(project).pipe(map(() => project));
        }),
      )
      .subscribe((project) => {
        this.router.navigate([`/projects/${project.id}`]);
        this.toastHelper.success(
          $localize`:@@toast.crmProject.overwriteSuccessful:Projekt '${project.name}:projectName:' wurde erfolgreich mit CRM-Daten überschrieben`,
        );
      });
  }

  private mapProject(): ScopedProject {
    const project = createProjectByScope(
      this.requireUserScope(),
      this.projectForm!.value.name,
      this.projectForm!.value.status,
      this.projectForm!.value.country,
    );
    project.version = this.normalizeOptionalString(this.projectForm!.value.version);
    project.status = this.projectForm!.value.status;
    project.country = this.projectForm!.value.country;
    if (isFgdsProject(project)) {
      project.forzaId = this.normalizeOptionalString(this.projectForm!.value.forzaId);
      project.region = this.projectForm!.value.region;
    }
    if (isMonitoringProject(project)) {
      project.draegerContactPerson = this.normalizeOptionalString(this.projectForm!.value.draegerContactPerson);
    }
    project.placeName = this.normalizeOptionalString(this.projectForm!.value.placeName);
    project.opportunityId =
      this.normalizeOptionalString(this.projectForm!.value.opportunityId) ??
      this.normalizeOptionalString(this.crmEntry?.opportunityId);
    project.opportunityLink =
      this.normalizeOptionalString(this.projectForm!.value.opportunityLink) ??
      this.normalizeOptionalString(this.crmEntry?.opportunityLink);
    project.crmEntryId = this.crmEntry?.id;
    project.notes = this.normalizeOptionalString(this.projectForm!.value.notes);

    this.mapFormFieldsToCustomer(project.customer, this.customerForm!.value);
    this.mapContactPerson(project.contactPerson, this.contactPersonForm!.value);
    if (this.goodsRecipientForm) this.mapGoodsRecipient(project.goodsRecipient, this.goodsRecipientForm!.value);
    this.mapFloorplans(project, this.floorplanFormComponent.floorplans);
    this.saveImages(project, this.imageUploadComponent.getImages());
    return project;
  }

  private saveImages(project: ScopedProject, images: ProjectImageData[]) {
    images.forEach((img) => {
      img.projectId = project.id;
    });
    this.projectImageService.saveImagesToIndexedDB(images, project).subscribe();
  }

  private mapFormFieldsToCustomer(customer: Customer, formValue: any) {
    customer.name = this.normalizeOptionalString(formValue.name);
    customer.customerNumber = this.normalizeOptionalString(formValue.customerNumber);
    customer.street = this.normalizeOptionalString(formValue.street);
    customer.zipCode = this.normalizeOptionalString(formValue.zipCode);
    customer.city = this.normalizeOptionalString(formValue.city);
  }

  private mapContactPerson(contactPerson: ContactPerson, formValue: any) {
    contactPerson.firstname = this.normalizeOptionalString(formValue.firstname);
    contactPerson.lastname = this.normalizeOptionalString(formValue.lastname);
    contactPerson.department = this.normalizeOptionalString(formValue.department);
    contactPerson.email = this.normalizeOptionalString(formValue.email);
    contactPerson.phoneNumber = this.normalizeOptionalString(formValue.phoneNumber);
  }

  private mapGoodsRecipient(goodsRecipient: GoodsRecipient, formValue: any) {
    goodsRecipient.information = this.normalizeOptionalString(formValue.information);
  }

  private mapFloorplans(project: ScopedProject, floorplans: UploadedFloorplan[]) {
    floorplans.forEach((floorplan) =>
      this.projectFloorplanService
        .saveFloorplanToIndexedDB(project, floorplan.name, floorplan.fileUrl, floorplan.description, floorplan.tags)
        .subscribe(),
    );
  }

  private requireUserScope(): BusinessScope {
    if (!isBusinessScope(this.userScope)) {
      throw new Error("User scope is not available");
    }
    return this.userScope;
  }
}
